import { useState, useMemo, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { FileBarChart, Calendar, ShoppingCart, Banknote, DollarSign, TrendingUp, ChevronDown, ChevronUp, X, Wallet } from "lucide-react";
import { Product } from "@/types/product";
import { Customer } from "@/types/customer";
import { PaymentHistory } from "@/types/payment";

interface ReportsPageProps {
  products: Product[];
  customers: Customer[];
}

interface DailyReport {
  date: string;
  salesCount: number;
  salesTotalIQD: number;
  salesTotalUSD: number;
  paidTotalIQD: number;
  paidTotalUSD: number;
  paymentsReceivedIQD: number; // پارەدانی وەرگیراو لە ڕۆژەکەدا
  paymentsReceivedUSD: number; // پارەدانی وەرگیراو لە ڕۆژەکەدا
  products: Product[];
  payments: PaymentHistory[]; // پارەدانەکانی ڕۆژەکە
}

export function ReportsPage({ products, customers }: ReportsPageProps) {
  const [startDate, setStartDate] = useState(() => {
    return new Date().toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [expandedDays, setExpandedDays] = useState<Set<string>>(new Set());
  const [expandedSales, setExpandedSales] = useState<Set<string>>(new Set());
  const [expandedPayments, setExpandedPayments] = useState<Set<string>>(new Set());
  const [paymentHistory, setPaymentHistory] = useState<PaymentHistory[]>([]);
  const [isLoadingPayments, setIsLoadingPayments] = useState(false);

  // API URL
  const getApiUrl = () => {
    return import.meta.env.VITE_KV_API_URL || 'https://qandil-realtime-api.qandil.workers.dev';
  };

  // خوێندنەوەی هەموو پارەدانەکان
  const fetchAllPayments = useCallback(async () => {
    setIsLoadingPayments(true);
    try {
      const token = localStorage.getItem('auth-token') || localStorage.getItem('auth_token') || import.meta.env.VITE_KV_TOKEN;
      const response = await fetch(`${getApiUrl()}/payments`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        setPaymentHistory(data);
      }
    } catch (error) {
      console.error('Error fetching payments:', error);
    } finally {
      setIsLoadingPayments(false);
    }
  }, []);

  // خوێندنەوەی پارەدانەکان کاتێک کۆمپۆنێنت لۆد دەبێت
  useEffect(() => {
    fetchAllPayments();
  }, [fetchAllPayments]);

  const toggleDayExpanded = (date: string) => {
    setExpandedDays(prev => {
      const newSet = new Set(prev);
      if (newSet.has(date)) newSet.delete(date);
      else newSet.add(date);
      return newSet;
    });
  };

  const toggleSalesExpanded = (date: string) => {
    setExpandedSales(prev => {
      const newSet = new Set(prev);
      if (newSet.has(date)) newSet.delete(date);
      else newSet.add(date);
      return newSet;
    });
  };

  const togglePaymentsExpanded = (date: string) => {
    setExpandedPayments(prev => {
      const newSet = new Set(prev);
      if (newSet.has(date)) newSet.delete(date);
      else newSet.add(date);
      return newSet;
    });
  };

  const getCustomerName = (customerId: string) => {
    return customers.find(c => c.id === customerId)?.fullName || "نەزانراو";
  };

  const dailyReports = useMemo(() => {
    const reports: Map<string, DailyReport> = new Map();
    
    // زیادکردنی فرۆشتنەکان
    products.forEach(product => {
      const saleDate = product.date?.split('T')[0] || new Date(product.createdAt).toISOString().split('T')[0];
      if (saleDate >= startDate && saleDate <= endDate) {
        if (!reports.has(saleDate)) {
          reports.set(saleDate, { 
            date: saleDate, 
            salesCount: 0, 
            salesTotalIQD: 0, 
            salesTotalUSD: 0, 
            paidTotalIQD: 0, 
            paidTotalUSD: 0,
            paymentsReceivedIQD: 0,
            paymentsReceivedUSD: 0,
            products: [],
            payments: []
          });
        }
        const report = reports.get(saleDate)!;
        report.salesCount++;
        report.products.push(product);
        if (product.currency === "IQD") {
          report.salesTotalIQD += product.salePrice;
          report.paidTotalIQD += product.paidAmount;
        } else {
          report.salesTotalUSD += product.salePrice;
          report.paidTotalUSD += product.paidAmount;
        }
      }
    });

    return Array.from(reports.values()).sort((a, b) => b.date.localeCompare(a.date));
  }, [products, startDate, endDate]);

  const dailyPaymentReports = useMemo(() => {
    const reports: Map<string, DailyReport> = new Map();

    // زیادکردنی پارەدانەکان
    paymentHistory.forEach(payment => {
      const paymentDate = payment.date?.split('T')[0];
      if (paymentDate && paymentDate >= startDate && paymentDate <= endDate) {
        if (!reports.has(paymentDate)) {
          reports.set(paymentDate, { 
            date: paymentDate, 
            salesCount: 0, 
            salesTotalIQD: 0, 
            salesTotalUSD: 0, 
            paidTotalIQD: 0, 
            paidTotalUSD: 0,
            paymentsReceivedIQD: 0,
            paymentsReceivedUSD: 0,
            products: [],
            payments: []
          });
        }
        const report = reports.get(paymentDate)!;
        report.payments.push(payment);
        if (payment.currency === "IQD") {
          report.paymentsReceivedIQD += payment.amount;
        } else {
          report.paymentsReceivedUSD += payment.amount;
        }
      }
    });

    return Array.from(reports.values()).sort((a, b) => b.date.localeCompare(a.date));
  }, [paymentHistory, startDate, endDate]);

  const totals = useMemo(() => {
    const salesTotals = dailyReports.reduce((acc, r) => ({
      salesCount: acc.salesCount + r.salesCount,
      salesTotalIQD: acc.salesTotalIQD + r.salesTotalIQD,
      salesTotalUSD: acc.salesTotalUSD + r.salesTotalUSD,
      paidTotalIQD: acc.paidTotalIQD + r.paidTotalIQD,
      paidTotalUSD: acc.paidTotalUSD + r.paidTotalUSD,
    }), { salesCount: 0, salesTotalIQD: 0, salesTotalUSD: 0, paidTotalIQD: 0, paidTotalUSD: 0 });

    const paymentTotals = dailyPaymentReports.reduce((acc, r) => ({
      paymentsReceivedIQD: acc.paymentsReceivedIQD + r.paymentsReceivedIQD,
      paymentsReceivedUSD: acc.paymentsReceivedUSD + r.paymentsReceivedUSD,
    }), { paymentsReceivedIQD: 0, paymentsReceivedUSD: 0 });

    return { ...salesTotals, ...paymentTotals };
  }, [dailyReports, dailyPaymentReports]);

  const formatDate = (dateStr: string) => new Date(dateStr).toLocaleDateString('ku', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });

  const clearDates = () => {
    const today = new Date().toISOString().split('T')[0];
    setStartDate(today);
    setEndDate(today);
  };

  return (
    <div className="space-y-4" dir="rtl">
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base"><Calendar className="h-5 w-5" />هەڵبژاردنی ماوە</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col sm:flex-row gap-4 items-end">
            <div className="flex-1 space-y-2">
              <Label>لە بەرواری</Label>
              <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} dir="ltr" />
            </div>
            <div className="flex-1 space-y-2">
              <Label>بۆ بەرواری</Label>
              <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} dir="ltr" />
            </div>
            <Button variant="outline" size="icon" onClick={clearDates} className="shrink-0" title="پاککردنەوە">
              <X className="h-4 w-4" />
            </Button>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
        <div className="rounded-xl border bg-card p-4">
          <div className="flex items-center gap-2 mb-2">
            <div className="h-8 w-8 rounded-lg bg-blue-500/10 flex items-center justify-center"><ShoppingCart className="h-4 w-4 text-blue-600" /></div>
          </div>
          <p className="text-2xl font-bold">{totals.salesCount}</p>
          <p className="text-xs text-muted-foreground">کۆی فرۆشتن</p>
        </div>
        <div className="rounded-xl border bg-card p-4">
          <div className="flex items-center gap-2 mb-2">
            <div className="h-8 w-8 rounded-lg bg-amber-500/10 flex items-center justify-center"><Banknote className="h-4 w-4 text-amber-600" /></div>
          </div>
          <p className="text-xl font-bold flex items-center gap-1">
            <span dir="ltr">{totals.salesTotalIQD.toLocaleString()}</span>
            <span className="text-xs font-normal text-muted-foreground">د.ع</span>
          </p>
          <p className="text-xs text-muted-foreground">فرۆشتن (دینار)</p>
        </div>
        <div className="rounded-xl border bg-card p-4">
          <div className="flex items-center gap-2 mb-2">
            <div className="h-8 w-8 rounded-lg bg-green-500/10 flex items-center justify-center"><DollarSign className="h-4 w-4 text-green-600" /></div>
          </div>
          <p className="text-xl font-bold" dir="ltr">${totals.salesTotalUSD.toLocaleString()}</p>
          <p className="text-xs text-muted-foreground">فرۆشتن (دۆلار)</p>
        </div>
        <div className="rounded-xl border bg-card p-4">
          <div className="flex items-center gap-2 mb-2">
            <div className="h-8 w-8 rounded-lg bg-purple-500/10 flex items-center justify-center"><Wallet className="h-4 w-4 text-purple-600" /></div>
          </div>
          <p className="text-xl font-bold flex items-center gap-1">
            <span dir="ltr">{totals.paymentsReceivedIQD.toLocaleString()}</span>
            <span className="text-xs font-normal text-muted-foreground">د.ع</span>
          </p>
          <p className="text-xs text-muted-foreground">وەرگیراو (دینار)</p>
        </div>
        <div className="rounded-xl border bg-card p-4">
          <div className="flex items-center gap-2 mb-2">
            <div className="h-8 w-8 rounded-lg bg-indigo-500/10 flex items-center justify-center"><Wallet className="h-4 w-4 text-indigo-600" /></div>
          </div>
          <p className="text-xl font-bold" dir="ltr">${totals.paymentsReceivedUSD.toLocaleString()}</p>
          <p className="text-xs text-muted-foreground">وەرگیراو (دۆلار)</p>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><FileBarChart className="h-5 w-5" />ڕاپۆرتی فرۆشتنی ڕۆژانە</CardTitle>
          <CardDescription>{dailyReports.length} ڕۆژ لە ماوەی دیاریکراو</CardDescription>
        </CardHeader>
        <CardContent>
          {dailyReports.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">هیچ فرۆشتنێک نییە بۆ ئەم ماوەیە</div>
          ) : (
            <div className="space-y-3">
              {dailyReports.map((report) => (
                <div key={report.date} className="border rounded-lg overflow-hidden">
                  <button onClick={() => toggleSalesExpanded(report.date)} className="w-full flex items-center justify-between p-4 hover:bg-muted/50 transition-colors">
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center"><ShoppingCart className="h-5 w-5 text-primary" /></div>
                      <div className="text-right">
                        <p className="font-semibold">{formatDate(report.date)}</p>
                        <p className="text-sm text-muted-foreground">{report.salesCount} فرۆشتن</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-left hidden sm:block">
                        {report.salesTotalIQD > 0 && <p className="text-sm"><span className="text-muted-foreground">فرۆشتن:</span> <span className="font-medium">{report.salesTotalIQD.toLocaleString()} د.ع</span></p>}
                        {report.salesTotalUSD > 0 && <p className="text-sm"><span className="text-muted-foreground">فرۆشتن:</span> <span className="font-medium">${report.salesTotalUSD.toLocaleString()}</span></p>}
                      </div>
                      {expandedSales.has(report.date) ? <ChevronUp className="h-5 w-5 text-muted-foreground" /> : <ChevronDown className="h-5 w-5 text-muted-foreground" />}
                    </div>
                  </button>
                  {expandedSales.has(report.date) && (
                    <div className="border-t bg-muted/30 p-4">
                      <div className="rounded-lg border bg-background overflow-x-auto">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead className="text-right">کڕیار</TableHead>
                              <TableHead className="text-right">کاڵا</TableHead>
                              <TableHead className="text-right">نرخ</TableHead>
                              <TableHead className="text-right">وەرگیراو</TableHead>
                              <TableHead className="text-right">ماوە</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {report.products.map((product) => (
                              <TableRow key={product.id}>
                                <TableCell>{getCustomerName(product.customerId)}</TableCell>
                                <TableCell>{product.deviceName}</TableCell>
                                <TableCell>
                                  <div className="flex items-center gap-1">
                                    <span dir="ltr">{product.salePrice.toLocaleString()}</span>
                                    <span className="text-xs text-muted-foreground">{product.currency === "IQD" ? "د.ع" : "$"}</span>
                                  </div>
                                </TableCell>
                                <TableCell className="text-green-600">
                                  <div className="flex items-center gap-1">
                                    <span dir="ltr">{product.paidAmount.toLocaleString()}</span>
                                    <span className="text-xs text-muted-foreground">{product.currency === "IQD" ? "د.ع" : "$"}</span>
                                  </div>
                                </TableCell>
                                <TableCell className="text-red-600">
                                  <div className="flex items-center gap-1">
                                    <span dir="ltr">{product.remainingAmount.toLocaleString()}</span>
                                    <span className="text-xs text-muted-foreground">{product.currency === "IQD" ? "د.ع" : "$"}</span>
                                  </div>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-purple-600"><Wallet className="h-5 w-5" />ڕاپۆرتی پارەدانی ڕۆژانە</CardTitle>
          <CardDescription>{dailyPaymentReports.length} ڕۆژ لە ماوەی دیاریکراو</CardDescription>
        </CardHeader>
        <CardContent>
          {dailyPaymentReports.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">هیچ پارەدانێک نییە بۆ ئەم ماوەیە</div>
          ) : (
            <div className="space-y-3">
              {dailyPaymentReports.map((report) => (
                <div key={report.date} className="border rounded-lg overflow-hidden">
                  <button onClick={() => togglePaymentsExpanded(report.date)} className="w-full flex items-center justify-between p-4 hover:bg-muted/50 transition-colors">
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-lg bg-purple-500/10 flex items-center justify-center"><Wallet className="h-5 w-5 text-purple-600" /></div>
                      <div className="text-right">
                        <p className="font-semibold">{formatDate(report.date)}</p>
                        <p className="text-sm text-muted-foreground">{report.payments.length} پارەدان</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-left hidden sm:block">
                        {report.paymentsReceivedIQD > 0 && <p className="text-sm text-purple-600"><span className="text-muted-foreground">وەرگیراو:</span> <span className="font-semibold">{report.paymentsReceivedIQD.toLocaleString()} د.ع</span></p>}
                        {report.paymentsReceivedUSD > 0 && <p className="text-sm text-purple-600"><span className="text-muted-foreground">وەرگیراو:</span> <span className="font-semibold">${report.paymentsReceivedUSD.toLocaleString()}</span></p>}
                      </div>
                      {expandedPayments.has(report.date) ? <ChevronUp className="h-5 w-5 text-muted-foreground" /> : <ChevronDown className="h-5 w-5 text-muted-foreground" />}
                    </div>
                  </button>
                  {expandedPayments.has(report.date) && (
                    <div className="border-t bg-muted/30 p-4">
                      <div className="rounded-lg border bg-background overflow-x-auto">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead className="text-right">بەروار</TableHead>
                              <TableHead className="text-right">کڕیار</TableHead>
                              <TableHead className="text-right">کاڵا</TableHead>
                              <TableHead className="text-right">بڕ</TableHead>
                              <TableHead className="text-right">جار</TableHead>
                              <TableHead className="text-right">ماوە دوای پارەدان</TableHead>
                              <TableHead className="text-right">تێبینی</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {report.payments.map((payment) => (
                              <TableRow key={payment.id}>
                                <TableCell>
                                  <span className="text-xs" dir="ltr">
                                    {new Date(payment.date).toLocaleDateString("ar-IQ")}
                                  </span>
                                </TableCell>
                                <TableCell>{getCustomerName(payment.customerId)}</TableCell>
                                <TableCell>{payment.productName}</TableCell>
                                <TableCell className="text-purple-600 font-semibold">
                                  <div className="flex items-center gap-1">
                                    <span dir="ltr">{payment.amount.toLocaleString()}</span>
                                    <span className="text-xs font-normal text-muted-foreground">{payment.currency === "IQD" ? "د.ع" : "$"}</span>
                                  </div>
                                </TableCell>
                                <TableCell>
                                  <span className="text-xs bg-purple-100 dark:bg-purple-950 text-purple-700 dark:text-purple-300 px-2 py-1 rounded">
                                    {payment.installmentNumber}
                                  </span>
                                </TableCell>
                                <TableCell className="text-orange-600">
                                  {payment.remainingAfterPayment !== undefined ? (
                                    <div className="flex items-center gap-1">
                                      <span dir="ltr">{payment.remainingAfterPayment.toLocaleString()}</span>
                                      <span className="text-xs font-normal text-muted-foreground">{payment.currency === "IQD" ? "د.ع" : "$"}</span>
                                    </div>
                                  ) : "-"}
                                </TableCell>
                                <TableCell className="text-sm text-muted-foreground">
                                  {payment.note || "-"}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
